// 用户权限枚举
export enum Permission {
  // 用户管理权限
  MANAGE_USERS = 'MANAGE_USERS',
  VIEW_ALL_USERS = 'VIEW_ALL_USERS',

  // 交易权限
  VIEW_ALL_TRANSACTIONS = 'VIEW_ALL_TRANSACTIONS',
  VIEW_OWN_TRANSACTIONS = 'VIEW_OWN_TRANSACTIONS',
  VIEW_ALL_ORDERS = 'VIEW_ALL_ORDERS',
  VIEW_OWN_ORDERS = 'VIEW_OWN_ORDERS',
  MANAGE_TRANSACTIONS = 'MANAGE_TRANSACTIONS',

  // 商户权限
  VIEW_ALL_MERCHANTS = 'VIEW_ALL_MERCHANTS',
  VIEW_OWN_MERCHANT_DATA = 'VIEW_OWN_MERCHANT_DATA',
  MANAGE_MERCHANTS = 'MANAGE_MERCHANTS',

  // 支付配置权限
  VIEW_PAYMENT_CONFIG = 'VIEW_PAYMENT_CONFIG',
  MANAGE_PAYMENT_CONFIG = 'MANAGE_PAYMENT_CONFIG',

  // 系统权限
  SYSTEM_MONITORING = 'SYSTEM_MONITORING',
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',

  // 财务权限
  VIEW_FINANCIAL_DATA = 'VIEW_FINANCIAL_DATA',
  MANAGE_PAYOUTS = 'MANAGE_PAYOUTS',

  // 代理权限
  VIEW_AGENT_MERCHANTS = 'VIEW_AGENT_MERCHANTS',
  VIEW_AGENT_STATISTICS = 'VIEW_AGENT_STATISTICS',
  MANAGE_AGENT_MERCHANTS = 'MANAGE_AGENT_MERCHANTS',
}

// 用户角色枚举
export enum UserRole {
  ADMIN = 'admin',
  MERCHANT = 'merchant',
  OPERATOR = 'operator',
  VIEWER = 'viewer',
  AGENT = 'agent',
}

// 用户接口
export interface User {
  id: string;
  username: string;
  fullName?: string;
  email?: string;
  role: UserRole;
  permissions: Permission[];
  merchantId?: string;
  // 代理相关字段
  agentId?: string;           // 所属代理ID（商户用）
  commissionRate?: number;    // 佣金比例（代理用，百分比，如5表示5%）
  managedMerchants?: string[]; // 代理管理的商户列表（代理用）
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING';
  isActive: boolean;
  isLocked?: boolean;
  loginAttempts?: number;
  loginAttemptsExpireAt?: string;
  lockUntil?: string;
  createdAt: string;
  updatedAt?: string;
  lastLoginAt?: string;
}

// API响应接口
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// 分页接口
export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T = any> extends ApiResponse<T> {
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// 兼容性类型别名
export type PaginationResponse<T = any> = PaginatedResponse<T>;

// 支付相关类型
export enum PaymentProviderType {
  NATIVE = 'NATIVE',
  WAKEUP = 'WAKEUP',
}

export type PaymentProviderCategory = 'native' | 'wakeup';

export interface PaymentProvider {
  id: string;
  name: string;
  displayName: string;
  type: PaymentProviderType;
  category: PaymentProviderCategory;
  isActive: boolean;
  features: string[];
  supportedCurrencies: string[];
  environment: 'development' | 'production';
  config: {
    apiKey: string;
    secretKey: string;
    accountId: string;
    webhookUrl: string;
    [key: string]: any;
  };
  fees?: {
    percentage: number;
    fixed: number;
  };
  limits?: {
    min: number;
    max: number;
    daily?: number;
    monthly?: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

// 商户信息接口
export interface Merchant {
  _id?: string;      // MongoDB文档ID
  merchantId: string;
  name: string;
  email: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  balance: {
    available: number;
    frozen: number;
  };
  settlement?: {
    mode: 'REALTIME' | 'T0';
    reserveAmount: number;
    timezone?: string;
    scheduleTimes?: string[];
    enabledAt?: string;
  };
  acceptance?: {
    exchangeRate: number;
  };
  paymentLinkRolloutEnabled?: boolean;
  collectionRefusalNoticeEnabled?: boolean;
  paymentLink?: {
    useCashgitWrapper: boolean;
    cashierType?: 'HOSTED' | 'ORIGINAL' | 'DEEPLINK';
    preferredLinkType?: 'PAYURL' | 'DEEPLINK';
    deepLinkMode?: 'HOSTED' | 'DIRECT';
  };
  userId?: string;
  username?: string;
  userFullName?: string;

  deposit: {
    native?: {
      fee: {
        percentage: number;
        fixedAmount: number;
      };
    };
    wakeup?: {
      fee: {
        percentage: number;
        fixedAmount: number;
      };
    };
    fee: {
      percentage: number;
      fixedAmount: number;
    };
    limits: {
      minAmount: number;
      maxAmount: number;
      dailyLimit: number;
      monthlyLimit: number;
      singleTransactionLimit: number;
    };
    usage: {
      dailyUsed: number;
      monthlyUsed: number;
      lastResetDate: string;
    };
  };

  withdrawal: {
    native?: {
      fee: {
        percentage: number;
        fixedAmount: number;
      };
    };
    wakeup?: {
      fee: {
        percentage: number;
        fixedAmount: number;
      };
    };
    fee: {
      percentage: number;
      fixedAmount: number;
    };
    limits: {
      minAmount: number;
      maxAmount: number;
      dailyLimit: number;
      monthlyLimit: number;
      singleTransactionLimit: number;
    };
    usage: {
      dailyUsed: number;
      monthlyUsed: number;
      lastResetDate: string;
    };
  };

  paymentConfigs?: string[];

  // 代收支付配置关联
  collectionConfigs?: string[];

  // 代付支付配置关联
  payoutConfigs?: string[];

  ipWhitelist?: {
    enabled: boolean;
    strictMode: boolean;
    allowedIPs: Array<{
      ip: string;
      mask?: number;
      description?: string;
      status?: 'ACTIVE' | 'INACTIVE';
    }>;
    accessRules: {
      blockUnknownIPs: boolean;
      maxFailedAttempts: number;
      lockoutDuration: number;
    };
  };

  createdAt: string;
  updatedAt: string;
}

// UPI支付信息接口
export interface UpiPaymentInfo {
  upiId: string;
  phoneNumber: string;
  accountName?: string;
  bankName?: string;
  ifscCode?: string;
  accountNumber?: string;
}

// 游戏订单信息接口
export interface GameOrderInfo {
  gameOrderId: string;
  gameName: string;
  gameType: 'RUMMY' | 'TEEN_PATTI' | 'OTHER';
  playerId: string;
  playerName?: string;
  tableId?: string;
  roomId?: string;
  betAmount?: number;
  winAmount?: number;
}

// 交互信息接口
export interface InteractionLogs {
  merchantRequest?: any;              // 商户发送的原始请求数据
  merchantResponse?: any;             // CashGit返回给商户的响应数据
  providerRequest?: any;              // 发送给支付商的请求数据
  providerResponse?: any;             // 支付商返回的响应数据
  providerCallback?: any;             // 支付商发送的回调数据
  requestTimestamp?: string;          // 商户请求时间
  responseTimestamp?: string;         // 响应时间
  providerRequestTimestamp?: string;  // 发送给支付商时间
  providerResponseTimestamp?: string; // 支付商响应时间
  callbackTimestamp?: string;         // 回调接收时间
}

// 订单信息接口
export interface Order {
  orderId: string;
  merchantId: string;
  type: 'DEPOSIT' | 'WITHDRAWAL' | 'WAKE_UP';
  amount: number;
  status: string;
  fee: number;
  netAmount: number;
  currency: string;
  customer: {
    email?: string;
    phone?: string;
    name?: string;
  };
  provider: {
    name: string;
    refId?: string;
  };
  upiPayment?: UpiPaymentInfo;
  gameOrder?: GameOrderInfo;
  returnUrl: string;
  notifyUrl?: string;
  interactionLogs?: InteractionLogs;  // 新增交互信息
  createdAt: string;
  updatedAt: string;
  error?: any;
}

// 交易记录接口
export interface Transaction {
  transactionId: string;
  orderId?: string;
  merchantId: string;
  type: 'DEPOSIT' | 'WITHDRAWAL' | 'REFUND' | 'ADJUSTMENT';
  amount: number;
  fee: number;
  netAmount: number;
  currency: string;
  balanceChange: number;
  balanceSnapshot: {
    before: number;
    after: number;
  };
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT' | 'CANCELLED' | 'REFUNDED' | 'PARTIAL_REFUNDED' | 'DISPUTED' | 'DISPUTE_RESOLVED' | 'RISK_BLOCKED' | 'MANUAL_REVIEW' | 'REVERSED' | 'EXPIRED';
  orderStatus?: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'CANCELLED' | 'TIMEOUT' | 'PARTIAL_SUCCESS' | 'REFUNDED' | 'PARTIAL_REFUNDED' | 'DISPUTED' | 'DISPUTE_RESOLVED' | 'RISK_BLOCKED' | 'MANUAL_REVIEW' | 'REVERSED' | 'EXPIRED';
  provider?: {
    name: string;
    refId?: string;
  };
  upiPayment?: UpiPaymentInfo;
  beneficiaryAccount?: string;
  beneficiaryName?: string;
  createdAt: string;
  updatedAt: string;
}

// 登录响应接口
export interface LoginResponse {
  user: User;
  token: string;
  permissions?: Permission[];
}

// 登录请求接口
export interface LoginRequest {
  username: string;
  password: string;
}

// CashGit支付API类型定义
export interface CashGitPaymentRequest {
  appid: string;
  orderid: string;
  amount: string;
  desc: string;
  notify_url: string;
  return_url?: string;
  user_mobile?: string;
  upi_id?: string;
  game_info?: string;
  sign: string;
}

export interface CashGitPaymentResponse {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

export interface CashGitQueryRequest {
  appid: string;
  orderid: string;
  sign: string;
}

export interface CashGitQueryResponse {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

export interface CashGitRefundRequest {
  appid: string;
  orderid: string;
  amount: string;
  desc: string;
  sign: string;
}

export interface CashGitRefundResponse {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

export interface CashGitCloseRequest {
  appid: string;
  orderid: string;
  sign: string;
}

export interface CashGitCloseResponse {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

// MG支付标准接口
export interface MGPaymentRequest {
  appid: string;
  orderid: string;
  amount: string;
  desc?: string;
  notify_url?: string;
  return_url?: string;
  sign: string;
}

export interface MGPaymentResponse {
  code: number;
  message: string;
  data: any;
  timestamp: number;
}

export interface MGPayResponseData {
  orderid: string;
  amount: string;
  payurl: string;
  qrcode: string;
  status: string;
}

export interface MGQueryResponseData {
  orderid: string;
  amount: string;
  status: string;
  paytime?: number;
  desc?: string;
}

export interface MGRefundRequest {
  appid: string;
  orderid: string;
  amount: string;
  sign: string;
}

export interface MGRefundResponseData {
  orderid: string;
  refund_amount: number;
  status: string;
}

// MG支付配置
export interface MGPaymentConfig {
  id: string;
  merchantId: string;
  appid: string;
  secretKey: string;
  apiUrl: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 角色权限映射
export interface RolePermissions {
  [UserRole.ADMIN]: Permission[];
  [UserRole.OPERATOR]: Permission[];
  [UserRole.MERCHANT]: Permission[];
}
